import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';

import { DataSurface } from '@/components/platform/DataSurface';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/hooks/use-toast';
import knowledgeGapsService from '@/lib/api/knowledgeGaps';
import { cn } from '@/lib/utils';

const SCOPE_OPTIONS = [
  {
    label: 'Recent 50',
    value: 50,
    detail: 'Fast scan',
  },
  {
    label: 'Recent 200',
    value: 200,
    detail: 'Recommended',
  },
  {
    label: 'Recent 500',
    value: 500,
    detail: 'Broader review',
  },
] as const;

interface TriggerPipelineTabProps {
  orgId: string;
}

export function TriggerPipelineTab({ orgId }: TriggerPipelineTabProps) {
  const [conversationLimit, setConversationLimit] = useState<number>(200);
  const queryClient = useQueryClient();

  const triggerRunMutation = useMutation({
    mutationFn: async () => {
      const res = await knowledgeGapsService.triggerRun(orgId, {
        max_traces: conversationLimit,
        skip_consolidation: false,
      });
      if (!res.success || !res.data) {
        const errorMsg = res.error || 'Failed to start gap analysis';
        if (errorMsg.toLowerCase().includes('already') || errorMsg.toLowerCase().includes('in progress')) {
          throw new Error('CONFLICT: ' + errorMsg);
        }
        throw new Error(errorMsg);
      }
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-runs', orgId] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-report', orgId] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-gaps-summaries', orgId] });
      toast({
        title: 'Gap search started',
        description: 'Hobbes is analyzing recent conversations.',
      });
    },
    onError: (error: Error) => {
      const isConflict = error.message.startsWith('CONFLICT:');
      toast({
        title: isConflict ? 'Search already running' : 'Failed to find gaps',
        description: isConflict
          ? 'Another gap search is already running. Please wait for it to finish.'
          : 'We could not start a gap search. Try again in a moment.',
        variant: isConflict ? undefined : 'destructive',
      });
    },
  });

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-[13px] font-medium text-foreground">Find gaps</h3>
        <p className="mt-0.5 text-xs text-muted-foreground">
          Analyze conversations to find questions your agent could not answer well.
        </p>
      </div>

      <DataSurface>
        <div className="grid gap-0 lg:grid-cols-[minmax(0,1fr)_340px]">
          <div className="border-b px-4 py-4 lg:border-b-0 lg:border-r">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline">Real sessions</Badge>
            </div>
            <h4 className="mt-3 text-sm font-medium text-foreground">Conversation source</h4>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-muted-foreground">
              Hobbes reviews recent conversations, groups recurring misses, and adds them to Open Gaps.
            </p>

            <div className="mt-5 space-y-2">
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Scope</p>
              <div className="grid gap-2 sm:grid-cols-3">
                {SCOPE_OPTIONS.map((option) => {
                  const selected = conversationLimit === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setConversationLimit(option.value)}
                      className={cn(
                        'rounded-lg border px-3 py-3 text-left transition-colors',
                        selected
                          ? 'border-foreground bg-background'
                          : 'border-border/80 bg-muted/20 hover:bg-muted/35'
                      )}
                    >
                      <p className="text-sm font-medium text-foreground">{option.label}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">{option.detail}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="flex flex-col justify-between gap-6 px-4 py-4">
            <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">What happens next</p>
              <div className="mt-3 space-y-3 text-sm text-muted-foreground">
                <p>1. Hobbes scans recent conversations.</p>
                <p>2. Repeated misses become open gaps.</p>
                <p>3. Review results in the Open Gaps tab.</p>
              </div>
            </div>

            <Button
              className="w-full"
              disabled={triggerRunMutation.isPending}
              onClick={() => triggerRunMutation.mutate()}
            >
              {triggerRunMutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Search className="mr-2 h-4 w-4" />
              )}
              Find Gaps
            </Button>
          </div>
        </div>
      </DataSurface>
    </div>
  );
}
